import { useContext, useEffect, useMemo } from 'react'
import { useLocation, useHistory, matchPath } from 'react-router-dom'
import { ALLOWED_COINS, ALLOWED_COINS_KEYS, UserContext } from '../Contexts/UserContext/UserContext.js'

// Token-scoped patterns: the token lives in the path as :token_id.
const TOKEN_SCOPED_PATTERNS = [
	'/wallet/:token_id',
	'/wallet/:token_id/balance',
	'/wallet/:token_id/mintuples',
	'/wallet/:token_id/history',
	'/wallet/:token_id/send',
	'/wallet/:token_id/swap',
	'/wallet/:token_id/admin/mint',
	'/wallet/:token_id/admin/mintuples',
	'/wallet/:token_id/admin/global-burn',
	'/wallet/:token_id/admin/burn',
	'/wallet/:token_id/admin/revoke-backup',
	'/wallet/:token_id/admin/create-contracts',
	'/wallet/:token_id/admin/swaps',
]

// Non-token-scoped wallet routes: tokenOrigin must not be resolved from these.
const NON_SCOPED_PATHNAMES = new Set(['/', '/home', '/wallet', '/wallet/settings'])

/**
 * Single source of truth for the currently selected token.
 *
 * Resolution order:
 *   1. `:token_id` path param of the current route (if it matches an allowed coin)
 *   2. `UserContext.selected_coin` (fallback)
 *   3. null -> redirect to the token picker home (`/wallet`)
 *
 * When the URL carries a valid `:token_id` that differs from the context,
 * the context is synced (so legacy components reading `get_selected_coin_props()`
 * stay correct). Components can keep reading the context; this hook just keeps
 * it in sync with the URL.
 *
 * Returns the resolved coin props (or null while redirecting / on non-scoped routes).
 */
export function useTokenOrigin() {
	const location = useLocation()
	const history = useHistory()
	const userContext = useContext(UserContext)

	const pathToken = useMemo(() => {
		// Static non-token-scoped paths (e.g. /wallet/settings) must never be
		// captured by the /wallet/:token_id pattern, otherwise "settings" would
		// be treated as a (bogus) token_id and fall back to the loaded coin.
		if (NON_SCOPED_PATHNAMES.has(location.pathname)) return null
		for (const pattern of TOKEN_SCOPED_PATTERNS) {
			const match = matchPath(location.pathname, { path: pattern, exact: true })
			if (match && match.params && match.params.token_id) {
				return match.params.token_id
			}
		}
		return null
	}, [location.pathname])

	const contextCoin = userContext?.selected_coin

	let resolvedKey = null
	if (pathToken && ALLOWED_COINS_KEYS.includes(pathToken)) {
		resolvedKey = pathToken
	} else if (contextCoin && ALLOWED_COINS_KEYS.includes(contextCoin)) {
		resolvedKey = contextCoin
	}

	// Sync context from the URL (URL is the primary source of truth)
	useEffect(() => {
		if (
			pathToken &&
			ALLOWED_COINS_KEYS.includes(pathToken) &&
			pathToken !== contextCoin
		) {
			userContext.update_selected_coin(pathToken)
		}
	}, [pathToken, contextCoin, userContext])

	// If we're on a token-scoped route but no coin can be resolved at all,
	// bail out to the token picker home.
	const tokenScoped = pathToken !== null
	useEffect(() => {
		if (tokenScoped && !resolvedKey) {
			history.replace('/wallet')
		}
	}, [tokenScoped, resolvedKey, history])

	return resolvedKey ? ALLOWED_COINS[resolvedKey] : null
}

// Re-exported for consumers that want to know if a pathname is token-scoped
// (e.g. to hide the token section in the sidebar on /wallet and /wallet/settings).
export function isTokenScopedPathname(pathname) {
	if (NON_SCOPED_PATHNAMES.has(pathname)) return false
	return TOKEN_SCOPED_PATTERNS.some(pattern =>
		matchPath(pathname, { path: pattern, exact: true })
	)
}

export default useTokenOrigin
